type StreamQuestHydrationQueueDependencies = {
    hydrateQuest: (questId: string) => Promise<unknown>
    needsHydration: (questId: string) => boolean
    now?: () => number
}

type StreamQuestHydrationQueueOptions = {
    delayMs?: number
    failureCooldownMs?: number
    maxConcurrency?: number
}

const DEFAULT_DELAY_MS = 16
const DEFAULT_FAILURE_COOLDOWN_MS = 60 * 1000
const DEFAULT_MAX_CONCURRENCY = 2

export function createStreamQuestHydrationQueue(
    dependencies: StreamQuestHydrationQueueDependencies,
    options: StreamQuestHydrationQueueOptions = {},
) {
    const delayMs = options.delayMs ?? DEFAULT_DELAY_MS
    const failureCooldownMs = options.failureCooldownMs ?? DEFAULT_FAILURE_COOLDOWN_MS
    const maxConcurrency = Math.max(1, options.maxConcurrency ?? DEFAULT_MAX_CONCURRENCY)
    const now = dependencies.now || Date.now
    const queuedIds = new Set<string>()
    const pendingIds = new Set<string>()
    const retryAfterById = new Map<string, number>()
    let activeCount = 0
    let generation = 0
    let timer: ReturnType<typeof setTimeout> | null = null

    function scheduleDrain(scheduleDelayMs = delayMs) {
        if (timer || queuedIds.size === 0 || activeCount >= maxConcurrency) return
        timer = setTimeout(drain, scheduleDelayMs)
    }

    function recordFailure(questId: string) {
        retryAfterById.set(questId, now() + failureCooldownMs)
    }

    function launch(questId: string) {
        const launchGeneration = generation
        activeCount += 1
        pendingIds.add(questId)

        dependencies
            .hydrateQuest(questId)
            .then(() => {
                if (dependencies.needsHydration(questId)) recordFailure(questId)
                else retryAfterById.delete(questId)
            })
            .catch(() => recordFailure(questId))
            .finally(() => {
                if (launchGeneration !== generation) return
                activeCount -= 1
                pendingIds.delete(questId)
                scheduleDrain(0)
            })
    }

    function drain() {
        timer = null

        while (activeCount < maxConcurrency && queuedIds.size > 0) {
            const questId = queuedIds.values().next().value as string | undefined
            if (!questId) break
            queuedIds.delete(questId)

            const retryAfter = retryAfterById.get(questId) || 0
            if (retryAfter > now()) continue
            if (!dependencies.needsHydration(questId)) {
                retryAfterById.delete(questId)
                continue
            }

            launch(questId)
        }

        scheduleDrain(0)
    }

    function queueStreamQuestHydration(
        questIds: Array<string | number | null | undefined>,
        queueOptions: { delayMs?: number } = {},
    ) {
        questIds
            .filter((questId): questId is string | number => questId != null)
            .map(String)
            .forEach(questId => {
                if (queuedIds.has(questId) || pendingIds.has(questId)) return
                const retryAfter = retryAfterById.get(questId) || 0
                if (retryAfter > now() || !dependencies.needsHydration(questId)) return
                queuedIds.add(questId)
            })

        scheduleDrain(queueOptions.delayMs ?? delayMs)
    }

    function reset() {
        generation += 1
        if (timer) clearTimeout(timer)
        timer = null
        activeCount = 0
        queuedIds.clear()
        pendingIds.clear()
        retryAfterById.clear()
    }

    return {
        queueStreamQuestHydration,
        reset,
    }
}
